import {
	useState,
	useEffect,
} from "react";

import { useLocation } from "react-router-dom";

import caseData from "../data/cases/cases.json";

import {
	getAvailableCaseFilters,
	normalizeCaseFilter,
} from "../data/cases/caseFilterHelpers";

const safeDecodeURIComponent = (
	value,
) => {
	try {
		return decodeURIComponent(
			value,
		);
	} catch (error) {
		return value;
	}
};

const validFilters = new Set([
	"all",
	...getAvailableCaseFilters(
		caseData,
	),
]);

const useFilterFromLocation = () => {
	const location = useLocation();

	const getFilterFromLocation = (
		location,
	) => {
		const pathname =
			location?.pathname ?? "";

		const locationSplit =
			pathname
				.split("/")
				.filter(Boolean)
				.map(
					safeDecodeURIComponent,
				);

		/*
		 * Direct project route:
		 *
		 * /fortnite
		 * /some-project
		 *
		 * Preserve old behavior:
		 * use that project's primary type
		 * as its filter context.
		 */
		if (
			locationSplit.length ===
				1 &&
			locationSplit[0] !==
				"cases"
		) {
			const shorthand =
				locationSplit[0];

			const maybeCase =
				caseData.find(
					(caseItem) =>
						caseItem.shorthand ===
						shorthand,
				);

			if (maybeCase) {
				return normalizeCaseFilter(
					maybeCase.type,
				);
			}
		}

		/*
		 * Cases routes:
		 *
		 * /cases
		 *
		 * /cases/work
		 *
		 * /cases/solutions
		 *
		 * /cases/virtual-production
		 *
		 * /cases/virtual-production/project-name
		 *
		 * Old encoded links also work:
		 *
		 * /cases/Virtual%20Production
		 */
		if (
			locationSplit[0] ===
			"cases"
		) {
			return normalizeCaseFilter(
				locationSplit[1] ??
					"all",
			);
		}

		return "all";
	};

	const validateFilter = (
		filter,
	) => {
		const normalizedFilter =
			normalizeCaseFilter(
				filter,
			);

		if (
			validFilters.has(
				normalizedFilter,
			)
		) {
			return normalizedFilter;
		}

		return "all";
	};

	const [
		filter,
		setFilter,
	] = useState(() =>
		validateFilter(
			getFilterFromLocation(
				location,
			),
		),
	);

	useEffect(() => {
		setFilter(
			validateFilter(
				getFilterFromLocation(
					location,
				),
			),
		);
	}, [location]);

	return filter;
};

export default useFilterFromLocation;